/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import type { Chunk } from './chunker'
import type { Embedding } from './embedding-service'

const DB_NAME = 'hivemind-rag'
const DB_VERSION = 2
const CHUNKS_STORE = 'chunks'
const EMBEDDINGS_STORE = 'embeddings'
const METADATA_STORE = 'metadata'

export interface ProjectMetadata {
  projectId: string
  projectName: string
  createdAt: number
  updatedAt: number
  totalChunks: number
  totalFiles: number
}

/**
 * Initialize RAG database
 */
function initDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve(request.result)

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result

      // Create chunks store
      if (!db.objectStoreNames.contains(CHUNKS_STORE)) {
        const chunksStore = db.createObjectStore(CHUNKS_STORE, { keyPath: 'id' })
        chunksStore.createIndex('projectId', 'projectId', { unique: false })
        chunksStore.createIndex('filePath', 'filePath', { unique: false })
        chunksStore.createIndex('hash', 'hash', { unique: false })
      }

      // Create embeddings store
      if (!db.objectStoreNames.contains(EMBEDDINGS_STORE)) {
        const embeddingsStore = db.createObjectStore(EMBEDDINGS_STORE, { keyPath: 'chunkId' })
        embeddingsStore.createIndex('model', 'model', { unique: false })
        embeddingsStore.createIndex('createdAt', 'createdAt', { unique: false })
      }

      // Create metadata store
      if (!db.objectStoreNames.contains(METADATA_STORE)) {
        const metadataStore = db.createObjectStore(METADATA_STORE, { keyPath: 'projectId' })
        metadataStore.createIndex('createdAt', 'createdAt', { unique: false })
        metadataStore.createIndex('updatedAt', 'updatedAt', { unique: false })
      }
    }
  })
}

/**
 * Save project metadata
 */
export async function saveProjectMetadata(metadata: ProjectMetadata): Promise<void> {
  const db = await initDB()
  const transaction = db.transaction([METADATA_STORE], 'readwrite')
  const store = transaction.objectStore(METADATA_STORE)

  store.put(metadata)

  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error)
  })
}

/**
 * Get project metadata
 */
export async function getProjectMetadata(projectId: string): Promise<ProjectMetadata | null> {
  const db = await initDB()
  const transaction = db.transaction([METADATA_STORE], 'readonly')
  const store = transaction.objectStore(METADATA_STORE)

  return new Promise((resolve, reject) => {
    const request = store.get(projectId)
    request.onsuccess = () => resolve(request.result || null)
    request.onerror = () => reject(request.error)
  })
}

/**
 * Get all project metadata, sorted by updatedAt (newest first)
 */
export async function getAllProjectMetadata(): Promise<ProjectMetadata[]> {
  const db = await initDB()
  const transaction = db.transaction([METADATA_STORE], 'readonly')
  const store = transaction.objectStore(METADATA_STORE)

  return new Promise((resolve, reject) => {
    const request = store.getAll()
    request.onsuccess = () => {
      const results = request.result || []
      // Sort by updatedAt descending (newest first)
      results.sort((a, b) => b.updatedAt - a.updatedAt)
      resolve(results)
    }
    request.onerror = () => reject(request.error)
  })
}

/**
 * Save chunks to IndexedDB
 */
export async function saveChunks(chunks: Chunk[]): Promise<void> {
  const db = await initDB()
  const transaction = db.transaction([CHUNKS_STORE], 'readwrite')
  const store = transaction.objectStore(CHUNKS_STORE)

  for (const chunk of chunks) {
    store.put(chunk)
  }

  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error)
  })
}

/**
 * Get chunks for a project
 */
export async function getChunks(projectId: string): Promise<Chunk[]> {
  const db = await initDB()
  const transaction = db.transaction([CHUNKS_STORE], 'readonly')
  const store = transaction.objectStore(CHUNKS_STORE)
  const index = store.index('projectId')

  return new Promise((resolve, reject) => {
    const request = index.getAll(projectId)
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

/**
 * Save embeddings to IndexedDB
 */
export async function saveEmbeddings(embeddings: Embedding[]): Promise<void> {
  const db = await initDB()
  const transaction = db.transaction([EMBEDDINGS_STORE], 'readwrite')
  const store = transaction.objectStore(EMBEDDINGS_STORE)

  for (const embedding of embeddings) {
    store.put(embedding)
  }

  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error)
  })
}

/**
 * Get embeddings for specific chunks
 */
export async function getEmbeddings(chunkIds: string[]): Promise<Embedding[]> {
  const db = await initDB()
  const transaction = db.transaction([EMBEDDINGS_STORE], 'readonly')
  const store = transaction.objectStore(EMBEDDINGS_STORE)

  const embeddings: Embedding[] = []

  for (const chunkId of chunkIds) {
    const request = store.get(chunkId)
    const embedding = await new Promise<Embedding | undefined>((resolve, reject) => {
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
    
    if (embedding) {
      embeddings.push(embedding)
    }
  }

  return embeddings
}

/**
 * Get all embeddings for a project
 */
export async function getProjectEmbeddings(projectId: string): Promise<Embedding[]> {
  const chunks = await getChunks(projectId)
  const chunkIds = chunks.map(c => c.id)
  return getEmbeddings(chunkIds)
}

/**
 * Delete all chunks, embeddings, and metadata for a project
 */
export async function deleteProjectRAG(projectId: string): Promise<void> {
  const db = await initDB()
  const chunks = await getChunks(projectId)

  // Delete chunks
  const chunksTransaction = db.transaction([CHUNKS_STORE], 'readwrite')
  const chunksStore = chunksTransaction.objectStore(CHUNKS_STORE)

  for (const chunk of chunks) {
    chunksStore.delete(chunk.id)
  }

  await new Promise<void>((resolve, reject) => {
    chunksTransaction.oncomplete = () => resolve()
    chunksTransaction.onerror = () => reject(chunksTransaction.error)
  })

  // Delete embeddings
  const embeddingsTransaction = db.transaction([EMBEDDINGS_STORE], 'readwrite')
  const embeddingsStore = embeddingsTransaction.objectStore(EMBEDDINGS_STORE)

  for (const chunk of chunks) {
    embeddingsStore.delete(chunk.id)
  }

  await new Promise<void>((resolve, reject) => {
    embeddingsTransaction.oncomplete = () => resolve()
    embeddingsTransaction.onerror = () => reject(embeddingsTransaction.error)
  })

  // Delete metadata
  const metadataTransaction = db.transaction([METADATA_STORE], 'readwrite')
  const metadataStore = metadataTransaction.objectStore(METADATA_STORE)
  metadataStore.delete(projectId)

  return new Promise((resolve, reject) => {
    metadataTransaction.oncomplete = () => resolve()
    metadataTransaction.onerror = () => reject(metadataTransaction.error)
  })
}

/**
 * Export index data (chunks + embeddings) to JSON
 */
export interface IndexExport {
  version: number
  projectId: string
  projectName: string
  createdAt: number
  chunks: Chunk[]
  embeddings: Embedding[]
  metadata: {
    totalChunks: number
    totalFiles: number
    embeddingModel: string
  }
}

export async function exportIndex(projectId: string, projectName: string): Promise<IndexExport> {
  const chunks = await getChunks(projectId)
  const embeddings = await getProjectEmbeddings(projectId)

  // Count unique files
  const uniqueFiles = new Set(chunks.map(c => c.filePath))

  return {
    version: 1,
    projectId,
    projectName,
    createdAt: Date.now(),
    chunks,
    embeddings,
    metadata: {
      totalChunks: chunks.length,
      totalFiles: uniqueFiles.size,
      embeddingModel: embeddings[0]?.model || 'text-embedding-3-small'
    }
  }
}

/**
 * Import index data from JSON
 */
export async function importIndex(data: IndexExport): Promise<void> {
  // Validate version
  if (data.version !== 1) {
    throw new Error(`Unsupported index version: ${data.version}`)
  }

  // Save chunks and embeddings
  await saveChunks(data.chunks)
  await saveEmbeddings(data.embeddings)

  console.log(`[RAG] Imported ${data.chunks.length} chunks and ${data.embeddings.length} embeddings`)
}

/**
 * List all indexed projects
 */
export async function listIndexedProjects(): Promise<Array<{ projectId: string; chunkCount: number; createdAt: number }>> {
  const db = await initDB()
  const transaction = db.transaction([CHUNKS_STORE, EMBEDDINGS_STORE], 'readonly')
  const chunksStore = transaction.objectStore(CHUNKS_STORE)

  return new Promise((resolve, reject) => {
    const request = chunksStore.getAll()
    request.onsuccess = () => {
      const allChunks = request.result as Chunk[]

      // Group by projectId
      const projectMap = new Map<string, { chunkCount: number; createdAt: number }>()

      for (const chunk of allChunks) {
        const existing = projectMap.get(chunk.projectId)
        if (existing) {
          existing.chunkCount++
        } else {
          projectMap.set(chunk.projectId, {
            chunkCount: 1,
            createdAt: Date.now() // We don't store creation time, so use current time
          })
        }
      }

      const result = Array.from(projectMap.entries()).map(([projectId, data]) => ({
        projectId,
        ...data
      }))

      resolve(result)
    }
    request.onerror = () => reject(request.error)
  })
}

